/**
 * Bounding-box scale gizmo: 8 handles (corners + side midpoints).
 * Dragging a handle scales the geometry; the opposite corner/side stays fixed.
 */
export type LonLat = readonly [number, number];

export type ScaleHandleId = 'sw' | 's' | 'se' | 'w' | 'e' | 'nw' | 'n' | 'ne';

export interface ScaleBbox {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

export interface ScaleHandle {
  readonly id: ScaleHandleId;
  readonly lonLat: LonLat;
}

const HANDLE_IDS: readonly ScaleHandleId[] = ['sw', 's', 'se', 'w', 'e', 'nw', 'n', 'ne'];

const OPPOSITE: Record<ScaleHandleId, ScaleHandleId> = {
  sw: 'ne',
  s: 'n',
  se: 'nw',
  w: 'e',
  e: 'w',
  nw: 'se',
  n: 's',
  ne: 'sw',
};

export function bboxFromGeometry(geometry: unknown): ScaleBbox | null {
  const points: LonLat[] = [];
  walkPositions(geometry, (lon, lat) => {
    points.push([lon, lat]);
  });
  if (points.length === 0) return null;
  let minX = points[0][0];
  let minY = points[0][1];
  let maxX = minX;
  let maxY = minY;
  for (const [lon, lat] of points) {
    if (lon < minX) minX = lon;
    if (lat < minY) minY = lat;
    if (lon > maxX) maxX = lon;
    if (lat > maxY) maxY = lat;
  }
  return { minX, minY, maxX, maxY };
}

export function scaleHandlesFromBbox(bbox: ScaleBBoxLike): ScaleHandle[] {
  const { minX, minY, maxX, maxY } = bbox;
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const byId: Record<ScaleHandleId, LonLat> = {
    sw: [minX, minY],
    s: [midX, minY],
    se: [maxX, minY],
    w: [minX, midY],
    e: [maxX, midY],
    nw: [minX, maxY],
    n: [midX, maxY],
    ne: [maxX, maxY],
  };
  return HANDLE_IDS.map((id) => ({ id, lonLat: byId[id] }));
}

type ScaleBBoxLike = ScaleBbox;

export function handleLonLat(bbox: ScaleBbox, id: ScaleHandleId): LonLat {
  return scaleHandlesFromBbox(bbox).find((handle) => handle.id === id)!.lonLat;
}

export function scaleGeometry(
  geometry: unknown,
  handleId: ScaleHandleId,
  toLonLat: LonLat,
): unknown {
  const bbox = bboxFromGeometry(geometry);
  if (!bbox) return geometry;
  const from = handleLonLat(bbox, handleId);
  const anchor = handleLonLat(bbox, OPPOSITE[handleId]);
  const lockX = handleId === 'n' || handleId === 's';
  const lockY = handleId === 'e' || handleId === 'w';
  const dxFrom = from[0] - anchor[0];
  const dyFrom = from[1] - anchor[1];
  const sx = lockX || Math.abs(dxFrom) < 1e-12 ? 1 : (toLonLat[0] - anchor[0]) / dxFrom;
  const sy = lockY || Math.abs(dyFrom) < 1e-12 ? 1 : (toLonLat[1] - anchor[1]) / dyFrom;
  const safeX = Number.isFinite(sx) && Math.abs(sx) > 1e-8 ? sx : 1;
  const safeY = Number.isFinite(sy) && Math.abs(sy) > 1e-8 ? sy : 1;
  return mapPositions(geometry, (lon, lat) => [
    anchor[0] + (lon - anchor[0]) * safeX,
    anchor[1] + (lat - anchor[1]) * safeY,
  ]);
}

export function bboxRing(bbox: ScaleBbox): LonLat[] {
  const { minX, minY, maxX, maxY } = bbox;
  return [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
    [minX, minY],
  ];
}

function walkPositions(value: unknown, visit: (lon: number, lat: number) => void): void {
  if (!value || typeof value !== 'object') return;
  const record = value as { type?: unknown; coordinates?: unknown; geometries?: unknown };
  if (Array.isArray(record.geometries)) {
    for (const child of record.geometries) walkPositions(child, visit);
    return;
  }
  walkCoords(record.coordinates, visit);
}

function walkCoords(coords: unknown, visit: (lon: number, lat: number) => void): void {
  if (!Array.isArray(coords) || coords.length === 0) return;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    visit(coords[0], coords[1]);
    return;
  }
  for (const child of coords) walkCoords(child, visit);
}

function mapPositions(value: unknown, map: (lon: number, lat: number) => LonLat): unknown {
  if (!value || typeof value !== 'object') return value;
  const record = value as { type?: unknown; coordinates?: unknown; geometries?: unknown };
  if (Array.isArray(record.geometries)) {
    return {
      ...record,
      geometries: record.geometries.map((child) => mapPositions(child, map)),
    };
  }
  if (!('coordinates' in record)) return value;
  return { ...record, coordinates: mapCoords(record.coordinates, map) };
}

function mapCoords(coords: unknown, map: (lon: number, lat: number) => LonLat): unknown {
  if (!Array.isArray(coords) || coords.length === 0) return coords;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    const next = map(coords[0], coords[1]);
    return coords.length > 2 ? [...next, ...coords.slice(2)] : [...next];
  }
  return coords.map((child) => mapCoords(child, map));
}
